import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
import { Menu, X, ChevronDown, User, LogOut, Plus } from 'lucide-react';
import DonationBanner from './info/DonationBanner';

const Navigation = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Cerrar el menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <>
      <nav className="bg-gradient-to-r from-blue-700 via-blue-800 to-blue-700 shadow-lg relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo y título */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/Salva.png"
                  className="h-16 w-16 object-contain bg-white rounded-full p-1 shadow-lg border-2 border-blue-200"
                  alt="Salva Patas Logo"
                />
                <div className="flex flex-col">
                  <span className="text-2xl font-bold text-white drop-shadow-lg">Salva</span>
                  <span className="text-2xl font-bold text-white drop-shadow-lg">Patas</span>
                  <span className="text-xs text-blue-200 font-semibold">Rescate y Adopción</span>
                </div>
              </Link>
            </div>

            {/* Enlaces de navegación - Desktop */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-white hover:text-blue-200 font-medium transition-colors">
                Inicio
              </Link>
              <Link to="/adopts" className="text-white hover:text-blue-200 font-medium transition-colors">
                Mascotas Adoptadas
              </Link>
              <Link to="/about" className="text-white hover:text-blue-200 font-medium transition-colors">
                Sobre Nosotros
              </Link>
              <Link to="/contact" className="text-white hover:text-blue-200 font-medium transition-colors">
                Contacto
              </Link>
              <Link to="/submit-pet" className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-full font-bold transition-all shadow-md hover:shadow-lg transform hover:scale-105">
                🐾 Publicar mi Rescate
              </Link>

              {/* Botones de autenticación */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-md hover:bg-white/20 font-medium border border-white/10"
                  >
                    <User size={20} />
                    <span className="max-w-[150px] truncate">{user.email}</span>
                    <ChevronDown
                      size={20}
                      className={`transform transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 w-64 mt-2 py-2 bg-white rounded-lg shadow-2xl z-50 border border-gray-200">
                      {user.email === 'admin@example.com' && (
                        <Link
                          to="/admin"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors"
                        >
                          <User size={18} />
                          <span>Panel Admin</span>
                        </Link>
                      )}
                      <Link
                        to="/admin/EditForm"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <User size={18} />
                        <span>Mi Perfil</span>
                      </Link>
                      <Link
                        to="/admin"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <Plus size={18} />
                        <span>Publicar mascota</span>
                      </Link>
                      <Link
                        to="/admin/timeline"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <Plus size={18} />
                        <span>Publicar Novedad</span>
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={18} />
                        <span>Cerrar Sesión</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link to="/signin" className="text-white hover:text-blue-100 font-medium">
                    Iniciar Sesión
                  </Link>
                
                </div>
              )}
            </div>

            {/* Botón menú móvil */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-white hover:bg-white/10"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Menú móvil */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gradient-to-r from-blue-800 to-blue-900 border-t border-blue-600/30">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                Inicio
              </Link>
              <Link to="/adopts" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                Mascotas Adoptadas
              </Link>
              <Link to="/about" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                Sobre Nosotros
              </Link>
              <Link to="/contact" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                Contacto
              </Link>
              <Link to="/submit-pet" className="block px-3 py-2 text-amber-300 hover:bg-white/10 rounded-md font-bold">
                🐾 Publicar mi Rescate
              </Link>
              
              {user ? (
                <>
                  <div className="border-t border-white/10 my-2"></div>
                  {user.email === 'admin@example.com' && (
                    <Link to="/admin" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                      Panel Admin
                    </Link>
                  )}
                  <Link to="/admin/EditForm" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                    Mi Perfil
                  </Link>
                  <Link to="/admin" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                    Publicar mascota
                  </Link>
                  <Link to="/admin/timeline" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                    Publicar Novedad
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-3 py-2 text-red-300 hover:bg-white/10 rounded-md font-medium"
                  >
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <>
                  <div className="border-t border-white/10 my-2"></div>
                  <Link to="/signin" className="block px-3 py-2 text-white hover:bg-white/10 rounded-md font-medium">
                    Iniciar Sesión
                  </Link>
                 
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <DonationBanner />
    </>
  );
};

export default Navigation;